import { alpha, Box, FormLabel, Stack, Typography } from '@mui/material';
import ComponentIcon from 'app/app/editor/tabs/components/ComponentIcon';
import { useAppBuilderContext } from 'app/app/editor/context';
import React, { useState } from 'react';
import CheckBoxSearch from 'components/common/check-box-list/CheckBoxSearch';
import useBundleTranslation from 'i18n';

import preview_container from './preview/example.png';
import preview_text from './preview/text.png';
import preview_image from './preview/image.png';
import preview_filter from './preview/filter.png';
import preview_announcements from './preview/announcements.png';
import preview_code from './preview/code.png';
import preview_elements from './preview/elements.png';
import preview_tile_panel from './preview/tile-panel.png';
import preview_folder_tree from './preview/folder-tree.png';
import preview_comment from './preview/comment.png';
import preview_mask from './preview/mask.png';
import preview_bar_chart from './preview/bar-chart.png';
import preview_bubble_chart from './preview/bubble-chart.png';
import preview_line_chart from './preview/line-chart.png';
import preview_format from './preview/format.png';
import preview_stock_chart from './preview/stock-chart.png';
import preview_concierge from './preview/concierge.png';
import { IMenuComponent } from 'app/app/editor/tabs/components/index';

// TODO: Replace CheckBoxSearch with custom 'Search' or Replace\Rename 'CheckBoxSearch' to become a generic component for search?
function Search(props: { onChange: (event: any) => void; value: string; placeholder?: string }) {
    return <CheckBoxSearch {...props} />;
}

export default function ComponentsTab() {
    const { t } = useBundleTranslation(['app/portal-page-builder']);
    const ctx = useAppBuilderContext();
    const [search, setSearch] = useState('');
    const [previewComponentType, setPreviewComponentType] = useState('');

    const alias_preview: any = {
        example: preview_container,
        text: preview_text,
        image: preview_image,
        filter: preview_filter,
        announcements: preview_announcements,
        code: preview_code,
        elements: preview_elements,
        'tile-panel': preview_tile_panel,
        'folder-tree': preview_folder_tree,
        comment: preview_comment,
        mask: preview_mask,
        'bar-chart': preview_bar_chart,
        'bubble-chart': preview_bubble_chart,
        'line-chart-with-arrow': preview_line_chart,
        format: preview_format,
        'stock-chart': preview_stock_chart,
        concierge: preview_concierge,
    };

    const alias_preview_title: any = {
        example: 'Grid container for arranging components',
        text: 'Flexible text element for titles and descriptions',
        image: 'Static image block for visual content or branding',
        filter: 'Dynamic filters to explore and segment content',
        announcements: 'Announcement block for news or alerts',
        code: 'Embedded Elements',
        elements: 'KPI summary panel with real-time indicators',
        'tile-panel': 'Visual grid with reports and metrics',
        'folder-tree': 'Folder navigation for reports',
        comment: 'Announcement block for news or alerts',
        mask: 'Area chart for representing data visually',
        'bar-chart': 'Bar chart for representing data visually',
        'bubble-chart': 'Bubble chart for representing data visually',
        'line-chart-with-arrow': 'Line chart for representing data visually',
        format: 'Pie chart for representing data visually',
        'stock-chart': 'Stock chart for representing data visually',
        concierge: 'Concierge is an AI assistant for quick info.',
    };

    const getName = (c: IMenuComponent) => {
        return c.title ?? t(`components_list.component_label.${c.name}`);
    };

    return (
        <Stack sx={{ py: 2, height: 1 }}>
            <Box sx={{ px: 2 }}>
                <Search value={search} onChange={(v?: string) => setSearch(v?.toLowerCase() ?? '')} />
            </Box>
            <Box sx={{ px: 2, flexGrow: 1, overflow: 'auto' }}>
                {ctx.componentsMenu
                    .filter(
                        (section) =>
                            section.components.filter((c) =>
                                t(`components_list.component_label.${c.name}`).toLowerCase().includes(search),
                            ).length != 0,
                    )
                    .map((s) => {
                        return (
                            <Box key={s.name} sx={{ pt: 2 }}>
                                <Box sx={{ fontWeight: 600, py: 0.5, mb: 0.5 }}>
                                    {s?.title ?? t(`components_list.section_heading.${s.name}`)}
                                </Box>
                                <Stack direction={'column'} spacing={0.5}>
                                    {s.components
                                        .filter((c) => getName(c).toLowerCase().includes(search))
                                        .map((c) => (
                                            <ComponentIcon
                                                type={c.name}
                                                key={c.name}
                                                icon={c.icon}
                                                setPreviewComponentType={setPreviewComponentType}
                                                title={getName(c)}
                                            />
                                        ))}
                                </Stack>
                            </Box>
                        );
                    })}
            </Box>
            <Stack
                direction={'row'}
                sx={{
                    p: 2,
                    height: '276px',
                    borderTop: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexShrink: 0,
                }}
            >
                {previewComponentType == '' ? (
                    <Box sx={{ color: (theme) => alpha(theme.palette.text.primary, 0.4) }}>
                        {t(`components_list.preview_placeholder`)}
                    </Box>
                ) : (
                    <Stack spacing={1}>
                        <Typography fontWeight={'bold'}>{alias_preview_title[previewComponentType]}</Typography>
                        <Box
                            component={'img'}
                            src={alias_preview[previewComponentType]}
                            sx={{ maxWidth: '100%', maxHeight: '100%' }}
                        />
                    </Stack>
                )}
            </Stack>
        </Stack>
    );
}
